import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ShieldCheck, Power, XCircle, Zap, RefreshCcw, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { PrimaryBtn, GhostBtn } from "@/pages/app/Dashboard";
import { StateBadge, Stepper } from "@/components/StateBadge";
import { OutcomePanel, ReauthPanel } from "@/pages/app/OutcomePanels";
import { SocialBar, ChatPanel } from "@/components/Social";
import { InterviewPanel } from "@/components/InterviewPanel";
import { useLiveEvent } from "@/context/RealtimeContext";
import { useApi } from "@/lib/useApi";

const Card = ({ title, children, testId }) => (
  <div className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid={testId}>
    <p className="mb-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">{title}</p>
    {children}
  </div>
);

const Party = ({ label, p, pid, confirmed }) => (
  <div className="flex-1">
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <Link to={`/app/profiles/${pid}`} className="mt-1 block font-display text-xl font-bold text-white hover:text-emerald-300" data-testid={`party-profile-link-${label}`}>{p.name}</Link>
    <p className="text-xs text-slate-400">{p.headline}</p>
    <p className="mt-2 text-xs text-slate-400">Trust <span className="font-mono text-emerald-300">{p.trust_score}</span> {confirmed && <CheckCircle2 className="ml-1 inline h-3.5 w-3.5 text-cyan-300" />}</p>
  </div>
);

export default function ConnectionDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { data: r, setData: setR, reload: load } = useApi(`/relationships/${id}`);
  useLiveEvent(["notification"], (m) => m.data.data?.relationship_id === id && ["state", "like"].includes(m.data.type) && load());

  const call = async (path, body) => {
    try { await api.post(`/relationships/${id}/${path}`, body); await load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!r) return <p className="font-mono text-xs text-slate-500">…</p>;
  const myConfirmed = r.my_sides.every((s) => r.confirmations[s]);

  return (
    <div className="space-y-6">
      <Link to="/app/connections" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-to-connections"><ArrowLeft className="h-4 w-4" />{t("connections_title")}</Link>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-bold text-white">{r.person.name} ↔ {r.company.name}</h1>
        <div className="flex items-center gap-3">
          <SocialBar r={r} onChange={(d) => setR((prev) => ({ ...prev, ...d }))} />
          <a href="#chat" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:text-emerald-300" data-testid="open-chat-btn"><MessageCircle className="h-4 w-4" />{t("chat")}</a>
          <span className="font-mono text-xs text-slate-500">{t("cycle")} {r.cycle}</span><StateBadge state={r.state} />
        </div>
      </div>
      <Stepper state={r.state} />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title={t("confirmations")} testId="parties-card">
          <div className="flex gap-6">
            <Party label={t("person")} p={r.person} pid={r.person_principal_id} confirmed={r.confirmations.person} />
            <Party label={t("company")} p={r.company} pid={r.company_principal_id} confirmed={r.confirmations.company} />
          </div>
        </Card>
        <Card title={`${t("score")} · ${t("breakdown")}`} testId="breakdown-card">
          <p className="font-display text-5xl font-bold text-emerald-300" data-testid="connection-score">{r.score}</p>
          <div className="mt-4 space-y-2">
            {Object.entries(r.breakdown).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3 text-xs">
                <span className="w-24 text-slate-400">{t(`br_${k}`)}</span>
                <div className="h-1 flex-1 rounded bg-white/10"><div className="h-1 rounded bg-emerald-400" style={{ width: `${v * 100}%` }} /></div>
                <span className="w-8 text-right font-mono text-slate-300">{Math.round(v * 100)}</span>
              </div>
            ))}
          </div>
          {!!r.matched_skills.length && <p className="mt-3 text-xs text-slate-400">✓ {r.matched_skills.join(", ")}</p>}
        </Card>
        <Card title={t("granted_actions")} testId="gate-card">
          <div className="flex flex-wrap gap-2">
            {r.state === "proposed" && (
              <PrimaryBtn disabled={myConfirmed} onClick={() => call("confirm")} data-testid="confirm-proposal-btn"><CheckCircle2 className="h-4 w-4" />{myConfirmed ? t("waiting_other") : t("confirm_proposal")}</PrimaryBtn>
            )}
            {r.state === "confirmed" && <PrimaryBtn onClick={() => call("authorize")} data-testid="server-authorize-btn"><ShieldCheck className="h-4 w-4" />{t("server_authorize")}</PrimaryBtn>}
            {r.state === "authorized" && <PrimaryBtn onClick={() => call("activate")} data-testid="activate-btn"><Power className="h-4 w-4" />{t("activate")}</PrimaryBtn>}
            {["proposed", "confirmed", "authorized", "active", "suspended"].includes(r.state) && (
              <GhostBtn onClick={() => call("terminate", { reason: "terminated_by_participant" })} data-testid="terminate-btn"><XCircle className="h-4 w-4" />{t("terminate")}</GhostBtn>
            )}
          </div>
          <div className="mt-5 space-y-2">
            {r.granted_actions.map((a) => (
              <div key={a} className="flex items-center justify-between rounded border border-white/5 px-3 py-2">
                <span className="text-sm text-slate-200">{t(`act_${a}`)}</span>
                <button onClick={() => call("actions", { action: a })} data-testid={`execute-action-${a}`}
                  className="inline-flex items-center gap-1 rounded-full border border-yellow-400/40 px-3 py-1 text-xs text-yellow-300 transition-colors hover:bg-yellow-400 hover:text-slate-950"><Zap className="h-3 w-3" />{t("execute")}</button>
              </div>
            ))}
            <p className="pt-1 text-xs text-slate-500">{t("shared_data")}: {r.shared_data.map((d) => t(`data_${d}`)).join(", ")}</p>
          </div>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <OutcomePanel r={r} onSaved={load} />
        <ReauthPanel r={r} onDone={load} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChatPanel r={r} />
        <InterviewPanel r={r} onScheduled={load} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={t("history")} testId="history-card">
          <ol className="space-y-3">
            {r.history.map((h, i) => (
              <li key={`${h.at}-${h.to}`} className="flex items-start gap-3 text-xs">
                <RefreshCcw className="mt-0.5 h-3 w-3 text-slate-500" />
                <div><StateBadge state={h.to} testId={`history-state-${i}`} /> <span className="ml-2 font-mono text-slate-500">{h.reason} · {h.actor} · {new Date(h.at).toLocaleString()}</span></div>
              </li>
            ))}
          </ol>
        </Card>
        <Card title={t("actions_log")} testId="actions-log-card">
          {!r.actions_log.length && <p className="text-sm text-slate-500">{t("empty")}</p>}
          <ul className="space-y-2">
            {r.actions_log.map((a, i) => <li key={`${a.at}-${a.action}`} className="font-mono text-xs text-slate-300" data-testid={`action-log-${i}`}>{t(`act_${a.action}`)} · {a.by} · {t("cycle")} {a.cycle} · {new Date(a.at).toLocaleString()}</li>)}
          </ul>
        </Card>
      </div>
    </div>
  );
}
